import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import CustomTable from "../../common/table/CustomTable";
import CustomPagination from "../../common/components/Pagination/CustomPagination";
import SelectFiled from "../../common/textfield/SelectFiled";
import SearchSelect from "../../common/textfield/SearchSelect";
import TextInput from "../../common/textfield/TextInput";
import { Button } from "../../common/Button";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { Col, Row } from "react-bootstrap";
import ClearIcon from "@mui/icons-material/Clear";
import { useFormik } from "formik";
import {
  DataFields,
  HeadCells,
  listSendOptions,
  DataFieldsFax,
  HeadCellsFax,
} from "./Constants";
import { failed } from "../../common/Toastify";
import { useOrgQuery } from "../../../hooks/ReactQueryHooks/useOrgQuery";
import { getSFTFullList } from "../../api/SFT";
import "./manage-sft.css";

const ROWS_PER_PAGE = 10;

const getDefaultType = () => "FAX";

function SFTList() {
  const [organizations, setOrganizations] = useState([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setisFetching] = useState(false);
  const [sftList, setSFTList] = useState([]);
  const [tableDataFields, setTableDataFields] = useState(DataFieldsFax);
  const [tableHeadCells, setTableHeadCells] = useState(HeadCellsFax);
  const pageTokensRef = useRef([null]);
  const userRoles = useSelector(
    (state) => state?.auth?.user?.["cognito:groups"]
  );
  const admin = userRoles?.includes("Super-Admin");
  const adminOrgs = useSelector((state) => state?.auth?.user?.organizations);

  const getDefaultOrg = () => (adminOrgs?.[0]?.id);

  const appliedFiltersRef = useRef({
    org: adminOrgs?.[0]?.id,
    type:  listSendOptions[1]?.value,
    encounterId: "",
  });

  const formik = useFormik({
    initialValues: {
      org: getDefaultOrg(),
      type: getDefaultType(),
      encounterId: "",
    },
  });

  const applyTableColumnsForType = (type) => {
    if (type === "Email") {
      setTableDataFields(DataFields);
      setTableHeadCells(HeadCells);
    } else {
      setTableDataFields(DataFieldsFax);
      setTableHeadCells(HeadCellsFax);
    }
  };

  const buildApiArgs = (pageIndex) => {
    const { org, type, encounterId } = appliedFiltersRef.current;
    const args = {
      type,
      limit: ROWS_PER_PAGE,
      pageNumber: pageIndex + 1,
    };

    if (pageIndex > 0 && pageTokensRef.current[pageIndex]) {
      args.nextToken = pageTokensRef.current[pageIndex];
    }

    if (encounterId?.trim()) {
      args.encounterId = encounterId.trim();
    }

    if (admin && org === "All") {
      return { ...args, isAllUsersAllOrgs: true };
    }

    return { ...args, orgId: org };
  };

  const parseListResponse = (res) => {
    const items = Array.isArray(res?.data) ? res.data : [];
    const nextToken = res?.nextToken ?? null;
    const total = res?.totalCount ?? null;

    return {
      items,
      nextToken,
      totalCount: total != null ? Number(total) : null,
      pageNumber: res?.pageNumber != null ? Number(res.pageNumber) : null,
    };
  };

  const applyPageResult = (pageIndex, { nextToken, totalCount: total }) => {
    pageTokensRef.current = pageTokensRef.current.slice(0, pageIndex + 1);
    pageTokensRef.current[pageIndex + 1] = nextToken ?? null;
    if (!nextToken) {
      pageTokensRef.current = pageTokensRef.current.slice(0, pageIndex + 2);
    }
    if (total != null && !Number.isNaN(total)) {
      setTotalCount(total);
    }
  };

  const fetchSFTPage = async (
    pageIndex,
    { resetTokens = false, updateTable = true } = {}
  ) => {
    if (resetTokens) {
      pageTokensRef.current = [null];
      pageIndex = 0;
      setTotalCount(0);
    }

    if (updateTable) {
      setIsLoading(true);
    }

    try {
      const res = await getSFTFullList(buildApiArgs(pageIndex));
      const parsed = parseListResponse(res);
      applyPageResult(pageIndex, parsed);

      if (updateTable) {
        setSFTList(parsed.items);
        setPage(pageIndex);
      }

      return parsed;
    } catch (res) {
      failed(
        res?.response?.data?.message ||
          res?.response?.data?.error ||
          res.message
      );
      throw res;
    } finally {
      if (updateTable) {
        setIsLoading(false);
        setisFetching(false);
      }
    }
  };

  const runSearch = async ({ org, type, encounterId }) => {
    appliedFiltersRef.current = {
      org,
      type,
      encounterId: encounterId?.trim() ?? "",
    };
    applyTableColumnsForType(type);
    await fetchSFTPage(0, { resetTokens: true });
  };

  useOrgQuery({
    onSuccess: (data) => {
      setOrganizations(
        data?.map((org) => ({ name: org?.name, value: org?.id }))
      );

      const defaultOrg = getDefaultOrg();
      const defaultType = getDefaultType();

      formik.setFieldValue("org", defaultOrg);
      formik.setFieldValue("type", defaultType);
      formik.setFieldValue("encounterId", "");

      runSearch({ org: defaultOrg, type: defaultType, encounterId: "" });
    },
    refetchOnWindowFocus: false,
  });

  const maxPageIndex = Math.max(
    0,
    Math.ceil(totalCount / ROWS_PER_PAGE) - 1
  );

  const handlePageChange = async (newPage) => {
    if (newPage < 0) return;
    if (totalCount > 0 && newPage > maxPageIndex) return;
    if (newPage === page) return;

    setisFetching(true);
    try {
      await fetchSFTPage(newPage);
    } finally {
      setisFetching(false);
    }
  };

  const handleSearch = () => {
    runSearch({
      org: formik.values.org,
      type: formik.values.type,
      encounterId: formik.values.encounterId,
    });
  };

  const handleReset = () => {
    const defaultOrg = getDefaultOrg();
    const defaultType = getDefaultType();

    formik.setFieldValue("org", defaultOrg);
    formik.setFieldValue("type", defaultType);
    formik.setFieldValue("encounterId", "");

    runSearch({ org: defaultOrg, type: defaultType, encounterId: "" });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    handleSearch();
  };

  const handleOrgChange = (selected) => {
    formik.setFieldValue("org", selected?.value || "");
  };

  const handleSendChange = (event) => {
    formik.setFieldValue("type", event?.target?.value);
  };

  const orgSelectOptions = (
    admin ? [{ name: "All", value: "All" }, ...organizations] : organizations
  ).map((o) => ({ label: o.name, value: o.value }));

  if (sftList) {
    sftList?.map((data) => {
      const modifiedPatientName = data?.subject?.name
        ?.split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      if (data?.subject) data.subject.name = modifiedPatientName;
      const modifiedPracName = data?.sender?.practitioner_name
        ?.split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      if (data?.sender) data.sender.practitioner_name = modifiedPracName;
      return data;
    });
  }

  return (
    <section className="common-listing">
      <form
        className="common-form border-fields encounterSearch"
        onSubmit={handleSearchSubmit}
        style={{ margin: "20px" }}
      >
        <Row>
          <Col md={2}>
            <TextInput
              keyField="encounterId"
              formik={formik}
              label={"Encounter Id"}
              placeholder="Encounter Id"
              hideRequired={true}
            />
          </Col>
          <Col md={2}>
            <SearchSelect
              options={orgSelectOptions}
              value={
                orgSelectOptions.find((o) => o.value === formik.values.org) ||
                null
              }
              onChange={handleOrgChange}
              placeholder="Select Organization"
              isDisabled={isLoading}
              formik={formik}
              keyField="org"
              hideRequired={true}
              isClearable={false}
              label={"Select Organization"}
            />
          </Col>
          <Col md={2}>
            <SelectFiled
              keyField={"type"}
              formik={formik}
              options={listSendOptions}
              onChange={handleSendChange}
              label={"Select Send Type"}
              required={false}
              optional={false}
              readOnly={isLoading || !formik?.values?.org}
            />
          </Col>
          <Col md={4}>
            <div className="btn-wrap">
              <Button type="submit" isLoading={isLoading}>
                <SearchOutlinedIcon /> Search
              </Button>
              <Button
                type="button"
                onClick={handleReset}
                style={{ marginLeft: "20px", marginRight: "0px" }}
                variant="secondary"
                title="Clear"
              >
                <ClearIcon /> Clear
              </Button>
            </div>
          </Col>
        </Row>
      </form>

      <CustomTable
        tableData={sftList}
        headCells={tableHeadCells}
        dataFields={tableDataFields}
        rowOptions={false}
        canView={false}
        canEdit={false}
        canDelete={false}
        canStatus={true}
        loading={isLoading}
        fetching={isFetching}
        page={0}
        setPage={() => {}}
        rowsPerPage={ROWS_PER_PAGE}
        hidePagination={true}
      />
      {totalCount > 0 || sftList?.length > 0 ? (
        <CustomPagination
          tableData={sftList}
          totalRows={totalCount > 0 ? totalCount : sftList.length}
          rowsPerPage={ROWS_PER_PAGE}
          page={page}
          handlePage={handlePageChange}
        />
      ) : null}
    </section>
  );
}

export default SFTList;
